import React, { type ReactNode, useContext } from 'react';

import type { FormDefinitionLike, TaxReturnRequestRuntime } from '../types';

export interface TaxReturnRuntimeValues<
  TDefinition extends FormDefinitionLike = FormDefinitionLike,
  TPeriod = object
> extends TaxReturnRequestRuntime<TDefinition, TPeriod> {
  readOnly: boolean;
}

export const defaultTaxReturnRuntimeValues: TaxReturnRuntimeValues = {
  formVersionId: undefined,
  def: undefined,
  taxpayerId: undefined,
  readOnly: false,
  period: undefined,
  amendedTaxReturnId: undefined
};

const getDefaultTaxReturnRuntimeValues = <
  TDefinition extends FormDefinitionLike = FormDefinitionLike,
  TPeriod = object
>(): TaxReturnRuntimeValues<TDefinition, TPeriod> =>
  ({
    ...defaultTaxReturnRuntimeValues
  }) as TaxReturnRuntimeValues<TDefinition, TPeriod>;

export const createTaxReturnRuntimeContext = <
  TDefinition extends FormDefinitionLike = FormDefinitionLike,
  TPeriod = object
>() =>
  React.createContext<TaxReturnRuntimeValues<TDefinition, TPeriod>>(
    getDefaultTaxReturnRuntimeValues<TDefinition, TPeriod>()
  );

interface TaxReturnRuntimeProviderProps<
  TDefinition extends FormDefinitionLike = FormDefinitionLike,
  TPeriod = object
> {
  children: ReactNode;
  context: React.Context<TaxReturnRuntimeValues<TDefinition, TPeriod>>;
  value: TaxReturnRuntimeValues<TDefinition, TPeriod>;
}

export const TaxReturnRuntimeProvider = <
  TDefinition extends FormDefinitionLike = FormDefinitionLike,
  TPeriod = object
>({
  children,
  context,
  value
}: TaxReturnRuntimeProviderProps<TDefinition, TPeriod>) => {
  const RuntimeContextProvider = context.Provider;

  return <RuntimeContextProvider value={value}>{children}</RuntimeContextProvider>;
};

export const useTaxReturnRuntimeValues = <
  TDefinition extends FormDefinitionLike = FormDefinitionLike,
  TPeriod = object
>(
  context: React.Context<TaxReturnRuntimeValues<TDefinition, TPeriod>>
) => useContext(context);
